import { Button, Col, Form, Input, Modal, Row, Select, Typography } from 'antd'
import { useContext, useRef, useState } from 'react'

import AppContext from '../common/AppContext'
import Message from '../common/Message'
import _ from 'lodash'
import { endOfDay } from 'date-fns'
import { formatDateTime } from '../../util/dateTimeUtil'
import { getServerDate } from '../../util/serverTimeUtil'
import { useEffect } from 'react'
import useResponsive from '../../hooks/useResponsive'

const { Paragraph } = Typography
const { TextArea } = Input

const DATETIME_FORMAT = 'yyyy-MM-dd HH:mm:ss'
const DATETIME_REGEX =
  /(19|20)\d{2}-(0[1-9]|1[0,1,2])-(0[1-9]|[12][0-9]|3[01]) (0[0-9]|1[0-9]|2[0-3]):[0-5]+[0-9]+:[0-5]+[0-9]+/g

const MessageModal = ({ visible, onOk, onCancel, messageToEdit }) => {
  const { currentUser } = useContext(AppContext)
  const [edittedMessage, setEdittedMessage] = useState({})
  const isNew = _.isNil(messageToEdit?.id)
  const [form] = Form.useForm()
  const bp = useResponsive()
  const updateTimerRef = useRef(null)

  const handleOk = () => {
    const fields = {
      ...(messageToEdit || {}),
      ...(edittedMessage || {}),
      ...form.getFieldsValue(true),
    }
    onOk(fields)
  }

  const handleCancel = () => {
    onCancel()
  }

  const getEndOfDayDateTime = () => formatDateTime(endOfDay(getServerDate()), DATETIME_FORMAT)

  const handleSetExpiresToEndOfDay = () => {
    form.setFieldsValue({ expires: getEndOfDayDateTime() })
  }
  const handleSetExpiresToNow = () => {
    form.setFieldsValue({ expires: formatDateTime(getServerDate(), DATETIME_FORMAT) })
  }

  const handleValuesChange = (_, allValues) => {
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current)
    }

    updateTimerRef.current = setTimeout(() => {
      setEdittedMessage({
        ...(messageToEdit || {}),
        ...allValues,
        ...(isNew ? { createdByUser: currentUser } : { updatedByUser: currentUser }),
      })
    }, 100)
  }

  useEffect(() => {
    if (visible) {
      form.resetFields()
      let fields
      if (messageToEdit) {
        const { expires } = messageToEdit
        fields = {
          ...messageToEdit,
          updatedByUser: currentUser,
          expires: formatDateTime(new Date(expires), DATETIME_FORMAT),
        }
        form.setFieldsValue(fields)
      } else {
        fields = {
          expires: getEndOfDayDateTime(),
          createdByUser: currentUser,
          visible: true,
        }
      }
      form.setFieldsValue(fields)
      setEdittedMessage(fields)
    }
  }, [visible, form, messageToEdit, isNew, currentUser])

  useEffect(() => {
    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current)
      }
    }
  }, [])

  const previewMessage = Object.keys(edittedMessage || {}).length > 0 ? edittedMessage : form.getFieldsValue(true)

  return (
    <Modal
      title={null}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={isNew ? 'Save' : 'Update message'}
      okButtonProps={{ type: 'primary' }}
      cancelButtonProps={{ type: 'link' }}
      visible={visible}
      closable={true}
      maskClosable={true}
      keyboard={true}
      destroyOnClose={true}
      width={bp.xs ? '100%' : '600px'}
    >
      <Paragraph className="messageModalHeader" level={3}>
        <>{isNew ? 'Create Message' : 'Update Message'}</>
      </Paragraph>
      <Form form={form} size="small" layout="vertical" colon={false} onValuesChange={handleValuesChange}>
        <Row gutter={10}>
          <Col>
            <Form.Item
              label="Expires"
              name="expires"
              style={{ marginBottom: 0 }}
              rules={[
                {
                  required: true,
                  message: 'Required',
                },
                {
                  pattern: DATETIME_REGEX,
                  message: `Enter a valid date/time (e.g. ${formatDateTime(getServerDate(), DATETIME_FORMAT)})`,
                },
              ]}
              getValueProps={(value) => {
                let result = value
                try {
                  if (value.match(DATETIME_REGEX)) {
                    result = formatDateTime(value, DATETIME_FORMAT)
                  }
                } catch (e) {
                  // Ignore
                }
                return { value: result }
              }}
            >
              <Input />
            </Form.Item>
            <Form.Item>
              <Button type="link" onClick={handleSetExpiresToEndOfDay} style={{ paddingLeft: 0 }}>
                Expire end of day today
              </Button>
              <Button type="link" onClick={handleSetExpiresToNow} style={{ paddingLeft: '1em' }}>
                Expire now
              </Button>
            </Form.Item>
          </Col>
          <Col flex={1}>
            <Form.Item
              label="Type"
              name="messageType"
              rules={[
                {
                  required: true,
                  message: 'Required',
                },
              ]}
            >
              <Select dropdownMatchSelectWidth>
                <Select.Option key="cancelledWeather">Rain</Select.Option>
                <Select.Option key="cancelledOther">Cancelled</Select.Option>
                <Select.Option key="heat">Warning</Select.Option>
                <Select.Option key="reminder">Reminder</Select.Option>
                <Select.Option key="general">Other</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={1}>
          <Col span={24}>
            <Form.Item
              label="Heading"
              name="heading"
              rules={[
                {
                  required: true,
                  message: 'Required',
                },
              ]}
            >
              <Input autoFocus placeholder="Message heading" />
            </Form.Item>
            <Form.Item label="Body (optional)" name="content">
              <TextArea placeholder="Message body (optional)" rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Preview">
          <Message message={previewMessage} adminMode={true} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default MessageModal
